import { useState } from "react";
import { Button, Icon, Notice, Skeleton, StatusBadge, TextField, Total } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { Textarea } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { parseArtikelCode } from "@shared/code";
import { formatDatum, formatEuro } from "./format";
import { useRueckgabeBuchen, useRueckgabeVorschau } from "./sonderfaelle-api";
import { OhneEtikett } from "./OhneEtikett";
import { useTastaturScanner } from "./scanner";

type Grund = "defekt" | "gefaellt_nicht" | "umtausch";

/** Rückgabe und Umtausch in vier Schritten: Artikel → Grund → Erstattung → Rückgabe-Kiste. */
export function Rueckgabe({ onZurueck, onFertig }: { onZurueck: () => void; onFertig: (betragCent: number) => void }) {
  const [code, setCode] = useState<string | null>(null);
  const [eingabe, setEingabe] = useState("");
  const [suche, setSuche] = useState(false);
  const [grund, setGrund] = useState<Grund>("defekt");
  const [erstattung, setErstattung] = useState<"sumup" | "bar">("sumup");
  const [bestaetigt, setBestaetigt] = useState(false);
  const [notiz, setNotiz] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [fotoFuer, setFotoFuer] = useState<{ id: string; betragCent: number } | null>(null);
  const [fotoLaeuft, setFotoLaeuft] = useState(false);
  const vorschau = useRueckgabeVorschau(code);
  const buchen = useRueckgabeBuchen();

  useTastaturScanner(!suche, (c) => {
    const p = parseArtikelCode(c);
    if (p) setCode(p.code);
  });

  if (suche) {
    return (
      <OhneEtikett
        status="verkauft"
        titel="Verkauften Artikel suchen"
        aktionText="Wählen"
        onZurueck={() => setSuche(false)}
        onWahl={(t) => {
          setCode(t.code);
          setSuche(false);
        }}
      />
    );
  }

  const v = vorschau.data;
  const vorschauFehler = vorschau.error instanceof ApiFehler ? vorschau.error.message : vorschau.isError ? "Artikel konnte nicht geladen werden." : null;
  const bereit = !!v && (erstattung === "bar" || bestaetigt);

  async function abschicken() {
    if (!code || !v) return;
    setFehler(null);
    try {
      const r = await buchen.mutateAsync({ code, grund, erstattungArt: erstattung, notiz, inSumupErstattet: bestaetigt });
      setFotoFuer({ id: r.rueckgabeId, betragCent: r.betragCent });
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Rückgabe konnte nicht gebucht werden.");
    }
  }

  if (fotoFuer) {
    return (
      <div className="sc-kasse">
        <section className="sc-kasse__cart kasse-flow">
          <h1 className="title">Rückgabe gebucht</h1>
          <Notice tone="success" title="Artikel liegt in der Rückgabe-Kiste">Der Mieter sieht das sofort im Portal. Ein Foto vom Artikel in der Kiste hilft ihm beim Abholen (freiwillig).</Notice>
          <div className="sc-toolbar">
            <label className="rg-btn rg-btn--secondary">
              {fotoLaeuft ? "Lädt …" : "Foto aufnehmen"}
              <input type="file" accept="image/*" capture="environment" hidden onChange={async (ev) => { const f = ev.target.files?.[0]; ev.target.value = ""; if (!f) return; setFotoLaeuft(true); try { const res = await fetch(`/api/kasse/rueckgabe/${fotoFuer.id}/foto`, { method: "PUT", headers: { "content-type": f.type || "image/jpeg" }, body: f, credentials: "same-origin" }); if (!res.ok) throw new Error("Foto konnte nicht gespeichert werden."); onFertig(fotoFuer.betragCent); } catch (err) { setFehler(err instanceof Error ? err.message : "Foto konnte nicht gespeichert werden."); } finally { setFotoLaeuft(false); } }} />
            </label>
            <Button variant="primary" onClick={() => onFertig(fotoFuer.betragCent)}>Ohne Foto fertig</Button>
          </div>
          {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        </section>
      </div>
    );
  }

  return (
    <div className="sc-kasse">
      <section className="sc-kasse__cart kasse-flow">
        <div className="sc-row">
          <Button variant="quiet" icon="chevron-left" onClick={onZurueck} aria-label="Zurück zum Warenkorb" />
          <h1 className="title">Rückgabe und Umtausch</h1>
        </div>
        <div className="sc-steps" aria-hidden="true">
          {["Artikel", "Grund", "Erstattung", "Kiste"].map((s, i) => (
            <span key={s} className={["sc-step", (i === 0 && !v) || (i === 1 && v) ? "is-on" : v && i === 0 ? "is-done" : ""].filter(Boolean).join(" ")}>
              <span className="sc-step__n">{i + 1}</span>
              {s}
            </span>
          ))}
        </div>
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}

        <h2 className="heading">1 · Artikel scannen oder suchen</h2>
        {!v ? (
          <div className="sc-row" style={{ alignItems: "flex-end" }}>
            <div className="sc-grow">
              <TextField label="Artikelcode" value={eingabe} onChange={(e) => setEingabe(e.target.value)} placeholder="1234-055-025" inputMode="numeric" data-scanner="true" error={vorschauFehler ?? undefined} onKeyDown={(e) => e.key === "Enter" && parseArtikelCode(eingabe) && setCode(parseArtikelCode(eingabe)!.code)} />
            </div>
            <Button variant="secondary" onClick={() => parseArtikelCode(eingabe) && setCode(parseArtikelCode(eingabe)!.code)} disabled={!parseArtikelCode(eingabe)}>
              Prüfen
            </Button>
            <Button variant="secondary" icon="search" onClick={() => setSuche(true)}>
              Suchen
            </Button>
          </div>
        ) : null}
        {vorschau.isPending && code ? <Skeleton lines={1} thumb /> : null}
        {v ? (
          <ItemRow
            title={v.artikel.bezeichnung}
            photo={v.artikel.fotoKey ? `/api/laden/dateien/${v.artikel.fotoKey}` : undefined}
            meta={`Regal ${v.artikel.regalNummer} · ${v.artikel.code} · ${v.artikel.mieterName} · ${v.position.zahlungsart === "bar" ? "Bar" : v.position.zahlungsart === "karte" ? "Karte" : "Zahlung"}`}
            status={<StatusBadge status="sold">Verkauft{v.position.bezahltAt ? ` am ${formatDatum(v.position.bezahltAt)}` : ""}</StatusBadge>}
            price={v.position.endpreisCent}
            action={
              <Button variant="quiet" icon="x" onClick={() => { setCode(null); setEingabe(""); setBestaetigt(false); }}>
                Anderer Artikel
              </Button>
            }
          />
        ) : null}

        {v ? (
          <>
            <h2 className="heading">2 · Grund</h2>
            <div className="sc-toolbar" role="radiogroup" aria-label="Grund">
              {([["defekt", "Defekt"], ["gefaellt_nicht", "Gefällt nicht"], ["umtausch", "Umtausch"]] as [Grund, string][]).map(([g, t]) => (
                <button key={g} type="button" className="sc-pill" aria-pressed={grund === g} onClick={() => setGrund(g)}>
                  {t}
                </button>
              ))}
            </div>

            <h2 className="heading">3 · {formatEuro(v.position.endpreisCent)} erstatten</h2>
            <div className="sc-toolbar" role="radiogroup" aria-label="Erstattung">
              <button type="button" className="sc-pill" aria-pressed={erstattung === "sumup"} onClick={() => setErstattung("sumup")}>
                In SumUp erstatten
              </button>
              <button type="button" className="sc-pill" aria-pressed={erstattung === "bar"} onClick={() => setErstattung("bar")}>
                Bar aus der Kasse
              </button>
            </div>
            {erstattung === "sumup" ? (
              <div className="sc-col">
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  <li>In der SumUp-App „Verkäufe“ öffnen.</li>
                  <li>
                    Die Zahlung {v.position.bezahltAt ? `vom ${formatDatum(v.position.bezahltAt)} ` : ""}über {formatEuro(v.position.endpreisCent)}
                    {v.position.kartendetails ? ` (${v.position.kartendetails})` : ""} wählen.
                  </li>
                  <li>„Erstatten“ tippen und {formatEuro(v.position.endpreisCent)} bestätigen.</li>
                </ol>
                <label className="sc-check">
                  <input type="checkbox" checked={bestaetigt} onChange={(e) => setBestaetigt(e.target.checked)} /> In SumUp erstattet
                </label>
                <p className="sc-muted sc-small">Die Erstattung erscheint in der Verwaltung unter Storno & Erstattungen und wird mit dieser Rückgabe verknüpft.</p>
              </div>
            ) : (
              <Notice tone="info">Der Betrag wird als Entnahme ins Kassenbuch geschrieben.</Notice>
            )}
            <Textarea label="Notiz (optional)" value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2} />
          </>
        ) : null}
      </section>
      <aside className="sc-kasse__side">
        <Total amountCent={v?.position.endpreisCent ?? 0} label="Erstattung" size="lg" caption={v ? (erstattung === "sumup" ? `Zurück per ${v.position.zahlungsart === "bar" ? "SumUp (Bar)" : "SumUp auf die Karte"}` : "Bar aus der Kasse") : "Noch kein Artikel gewählt"} />
        {v ? (
          <Notice tone="info" icon="archive" title="4 · Artikel in die Rückgabe-Kiste legen">
            Der Mieter räumt selbst wieder ein. {v.artikel.mieterName} wird informiert; {formatEuro(v.position.endpreisCent)} werden in der Abrechnung abgezogen.
          </Notice>
        ) : (
          <p className="kasse-side__hint">
            <Icon name="info" size={16} /> Etikett scannen oder den Artikel suchen. Es geht nur bei verkauften Artikeln.
          </p>
        )}
        <span className="sc-grow" />
        <Button variant="primary" size="lg" block icon="undo-2" disabled={!bereit} loading={buchen.isPending} onClick={() => void abschicken()}>
          Rückgabe buchen
        </Button>
        <Button variant="quiet" block onClick={onZurueck}>
          Abbrechen
        </Button>
      </aside>
    </div>
  );
}
